/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, useMemo, useState } from "react";
import ArgosFlexbox from "../../../../components/Containers/ArgosFlexbox";
import { MRT_ColumnDef, MRT_RowSelectionState } from "material-react-table";
import ADAS_PROGRAMS_CONSTANTS_ from "../../constants";
import { usePostRequest } from "../../../../../api/helper";
import { AdasProgramsReportPages } from "../common/constants";
import toast from "react-hot-toast";
import Loader from "../../../../components/Loader/Loader";
import {ArgosTableSimple} from "../../../../components/Table/ArgosTableSimple";


interface GreenLineObjectsData {
  objectId: string
  objectName: string
  objectType: string
}

interface FetchGreenLineObjectsResp {
  objectIds: string[]
  objectData: any
}

interface SelectGreenLineObjectsProps {
  dataSets: MRT_RowSelectionState
  selectedObjects: MRT_RowSelectionState
  setSelectedObjects: React.Dispatch<React.SetStateAction<MRT_RowSelectionState>>
}

/**
 * This wizard step allows user to select the objects of the green line data set
 * to use when creating a report.
 */
export const SelectGreenLineObjects = (props: SelectGreenLineObjectsProps) => {
  const { dataSets, selectedObjects, setSelectedObjects } = props

  const greenLineObjectTableColumns = useMemo<MRT_ColumnDef<GreenLineObjectsData>[]>(
    () => [
      {
        header: "Object Type",
        accessorKey: 'objectType',
      },
      {
        header: "Object Name",
        accessorKey: 'objectName',
      },
    ],
    [],
  )

  const fetchGreenLineObjectsUrl = ADAS_PROGRAMS_CONSTANTS_.itemDetails.sensorComponentRequirements.endpoints.fetchGreenLineObjects
  const fetchGreenLineObjectsHandle = usePostRequest(fetchGreenLineObjectsUrl)
  const [isLoadingFetchGreenLineObjects, setIsLoadingFetchGreenLineObjects] = useState<boolean>(false)
  const [greenLineObjectData, setGreenLineObjectData] = useState<any>({})

  const fetchGreenLineObjects = () => {
    setIsLoadingFetchGreenLineObjects(true)
    const body = {greenLineDataSetId: Object.keys(dataSets)[0], page: AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT}
    fetchGreenLineObjectsHandle(body).then((resp: FetchGreenLineObjectsResp) => {
      setGreenLineObjectData(resp.objectData)
    }).catch((err) => {
      console.log(err)
      toast.error('An error occurred while fetching the green line objects')
    }).finally(() => {
      setIsLoadingFetchGreenLineObjects(false)
    })
  }

  /**
   * Fetch green line objects when a data set is selected or selection changes
   */
  useEffect(() => {
    if (Object.keys(dataSets).length > 0) {
      fetchGreenLineObjects()
    }
  }, [dataSets])

  return (
    <ArgosFlexbox vertical style={{height: '100%', width: '100%'}}>
      <Loader isLoading={isLoadingFetchGreenLineObjects}>
        <ArgosTableSimple
          data={greenLineObjectData}
          columns={greenLineObjectTableColumns}

          /** State management */
          layoutMode='semantic'
          getRowId={(row) => row.objectId}
          state={{rowSelection: selectedObjects}}
          enablePagination={false}

          /** Row selection */
          enableRowSelection={true}
          onRowSelectionChange={setSelectedObjects}
        />
      </Loader>
    </ArgosFlexbox>
  )
}
